import { useState, useEffect, useCallback } from 'react'
import {
  MagnifyingGlassIcon, ArrowPathIcon, CheckCircleIcon,
  XCircleIcon, PaperAirplaneIcon, TicketIcon,
} from '@heroicons/react/24/outline'
import { ticketsApi, type AdminTicket } from '@/api/tickets'
import { toast } from '@/store/toastStore'
import { Spinner } from '@/components/ui/Spinner'

// ─── Formatters ───────────────────────────────────────────────────────────────

function formatFCFA(n: number) {
  return `${n.toLocaleString('fr-FR')} FCFA`
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  pending:   'En attente',
  active:    'Actif',
  used:      'Utilisé',
  cancelled: 'Annulé',
  expired:   'Expiré',
}

const STATUS_CLASSES: Record<string, string> = {
  pending:   'bg-warning-50 text-warning-600',
  active:    'bg-success-50 text-success-700 border border-success-200',
  used:      'bg-gray-100 text-gray-500',
  cancelled: 'bg-danger-50 text-danger-600',
  expired:   'bg-gray-100 text-gray-400',
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_CLASSES[status] ?? 'bg-gray-100 text-gray-500'}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  )
}

// ─── Skeleton row ─────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {Array.from({ length: 8 }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 bg-gray-100 rounded w-full" />
        </td>
      ))}
    </tr>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function TicketListPage() {
  const [tickets, setTickets] = useState<AdminTicket[]>([])
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 20 })
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [sending, setSending] = useState<string | null>(null)

  const load = useCallback(async (p = 1) => {
    setLoading(true)
    try {
      const result = await ticketsApi.list({
        page: p,
        per_page: 20,
        phone: search.trim() || undefined,
        status: statusFilter || undefined,
      })
      setTickets(result.data)
      setMeta(result.meta)
    } catch {
      toast.error('Impossible de charger les tickets.')
    } finally {
      setLoading(false)
    }
  }, [search, statusFilter])

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1) }, 300)
    return () => clearTimeout(t)
  }, [search, statusFilter])

  useEffect(() => { load(page) }, [page])

  async function handleResend(uuid: string) {
    setSending(uuid)
    try {
      await ticketsApi.resendWhatsApp(uuid)
      toast.success('Renvoi WhatsApp en cours.')
    } catch {
      toast.error('Échec du renvoi WhatsApp.')
    } finally {
      setSending(null)
    }
  }

  return (
    <div className="p-6 space-y-5">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Tickets</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {meta.total.toLocaleString('fr-FR')} ticket{meta.total > 1 ? 's' : ''} au total
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher par téléphone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-gray-700"
        >
          <option value="">Tous les statuts</option>
          <option value="active">Actif</option>
          <option value="used">Utilisé</option>
          <option value="pending">En attente</option>
          <option value="cancelled">Annulé</option>
        </select>

        <button
          onClick={() => load(page)}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/60">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Acheteur</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Événement</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Catégorie</th>
                <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Qté</th>
                <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Montant</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Statut</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">WhatsApp</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Acheté le</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading
                ? Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
                : tickets.length === 0
                  ? (
                    <tr>
                      <td colSpan={9}>
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                          <TicketIcon className="w-10 h-10 mb-2 opacity-30" />
                          <p className="text-sm font-medium">Aucun ticket trouvé</p>
                          {(search || statusFilter) && (
                            <p className="text-xs mt-1">Essayez de modifier vos filtres</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                  : tickets.map((ticket) => (
                    <tr key={ticket.uuid} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{ticket.buyer_name ?? '—'}</p>
                        <p className="text-xs text-gray-400 font-mono">{ticket.buyer_phone}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-700 max-w-[180px]">
                        <span className="line-clamp-2">{ticket.event_name ?? '—'}</span>
                      </td>
                      <td className="px-4 py-3">
                        {ticket.ticket_type ? (
                          <span className="inline-flex px-2 py-0.5 bg-primary-50 text-primary-700 rounded text-xs font-semibold">
                            {ticket.ticket_type}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-gray-700">{ticket.quantity}</td>
                      <td className="px-4 py-3 text-right font-mono text-gray-900 font-medium whitespace-nowrap">
                        {formatFCFA(ticket.total_price)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={ticket.status} />
                      </td>
                      <td className="px-4 py-3">
                        {ticket.whatsapp_sent
                          ? <CheckCircleIcon className="w-5 h-5 text-success-500" title="Envoyé" />
                          : <XCircleIcon className="w-5 h-5 text-gray-300" title="Non envoyé" />}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {formatDate(ticket.purchased_at)}
                      </td>
                      <td className="px-4 py-3">
                        {ticket.status === 'active' && (
                          <button
                            onClick={() => handleResend(ticket.uuid)}
                            disabled={sending === ticket.uuid}
                            title="Renvoyer sur WhatsApp"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                          >
                            {sending === ticket.uuid
                              ? <Spinner className="w-3 h-3" />
                              : <PaperAirplaneIcon className="w-3.5 h-3.5" />}
                            WhatsApp
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {meta.last_page > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/40">
            <p className="text-xs text-gray-500">
              Page {meta.current_page} sur {meta.last_page}
              {' '}· {meta.total.toLocaleString('fr-FR')} résultats
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={meta.current_page === 1}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-white disabled:opacity-40 transition-colors"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
                disabled={meta.current_page === meta.last_page}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-white disabled:opacity-40 transition-colors"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
